import React, { useEffect, useState } from 'react';
import Loader from '../../components/Loader';
import Policy_Navbar from './PolicyNavbar';
import Calling from '../../api/Calling';
import { toast } from 'react-toastify';
import { MdOutlineEdit } from "react-icons/md";
import { IoSaveSharp } from "react-icons/io5";
import { FiFileText, FiType, FiAlignLeft } from "react-icons/fi";

const IosTerms = () => {
    const [loader, setLoader] = useState(false);
    const [policy, setPolicy] = useState({});
    const [isEditing, setIsEditing] = useState(false);
    const [editedPolicy, setEditedPolicy] = useState({
        terms_title: '',
        terms_description: ''
    });

    const fetchData = async () => {
        setLoader(true);
        try {
            const resp = await Calling.getIosTerms();
            if (resp?.status) {
                setPolicy(resp?.data);
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setLoader(false);
        }
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditedPolicy(prev => ({
            ...prev,
            [name]: value
        }));
    }

    const handleSave = async () => {
        setLoader(true);
        try {
            const resp = await Calling.updateIosTerms(editedPolicy);
            if (resp?.status) {
                toast.success('Terms & Conditions updated successfully');
                setPolicy(editedPolicy);
                setIsEditing(false);
            } else {
                toast.error(resp?.message);
            }
        } catch (err) {
            console.log(err);
            toast.error('Failed to update terms');
        } finally {
            setLoader(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        setEditedPolicy({
            terms_title: policy?.terms_title || '',
            terms_description: policy?.terms_description || ''
        });
    }, [policy]);

    return (
        <>
            {loader ? <Loader /> : (
                <div className="w-full min-h-screen text-main-font">
                    {/* Page Header */}
                    <div className="mb-6">
                        <div className="flex items-center gap-3">
                            <div className="p-2.5 rounded-xl bg-main-blue text-main-white">
                                <FiFileText size={24} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-main-font">Terms & Conditions</h1>
                                <p className="text-sm text-main-subfont">Manage iOS app terms & conditions</p>
                            </div>
                        </div>
                    </div>

                    {/* Main Card */}
                    <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                        {/* Navigation */}
                        <div className="p-4 border-b border-main-border">
                            <Policy_Navbar
                                selectState={'iosterms'}
                                onClick={() => isEditing ? handleSave() : setIsEditing(true)}
                                label={isEditing ? 'Save' : 'Edit'}
                                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                                    isEditing 
                                        ? 'bg-main-blue text-main-white hover:bg-main-blue/90' 
                                        : 'bg-main-white text-main-delete border border-main-delete hover:bg-main-delete/5'
                                }`}
                                icon={isEditing ? <IoSaveSharp size={16} /> : <MdOutlineEdit size={16} />}
                                btnSave={isEditing}
                            />
                        </div>

                        {/* Content */}
                        <div className="p-6">
                            {/* Editing Badge */}
                            {isEditing && (
                                <div className="mb-6 p-3 rounded-xl bg-main-th border border-main-blue/20 flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-main-blue animate-pulse"></div>
                                    <span className="text-sm text-main-font">
                                        <span className="font-medium text-main-blue">Edit Mode:</span> Make changes to your terms & conditions below
                                    </span>
                                </div>
                            )}

                            {/* Title Section */}
                            <div className="mb-6">
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-main-subfont">
                                            <FiType size={16} />
                                            Terms Title
                                        </label>
                                        <input
                                            type="text"
                                            name="terms_title"
                                            value={editedPolicy.terms_title}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-main-border rounded-xl bg-main-bg text-main-font focus:bg-main-white focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all"
                                            placeholder="Enter Terms & Conditions Title"
                                        />
                                    </div>
                                ) : (
                                    <div className="pb-4 border-b border-main-border">
                                        <h2 className="text-xl sm:text-2xl font-bold text-main-font break-words">
                                            {policy?.terms_title || 'No title set'}
                                        </h2>
                                    </div>
                                )}
                            </div>

                            {/* Description Section */}
                            <div>
                                {isEditing ? (
                                    <div className="space-y-2">
                                        <label className="flex items-center gap-2 text-sm font-medium text-main-subfont">
                                            <FiAlignLeft size={16} />
                                            Terms Description
                                        </label>
                                        <textarea
                                            name="terms_description"
                                            value={editedPolicy.terms_description}
                                            onChange={handleInputChange}
                                            className="w-full h-[300px] sm:h-[400px] px-4 py-3 border border-main-border rounded-xl bg-main-bg text-main-font focus:bg-main-white focus:outline-none focus:ring-2 focus:ring-main-blue/20 focus:border-main-blue transition-all resize-none"
                                            placeholder="Enter Terms & Conditions Description"
                                        />
                                    </div>
                                ) : (
                                    <div className="p-4 rounded-xl bg-main-bg border border-main-border">
                                        <pre className="text-sm sm:text-base text-main-font break-words whitespace-pre-wrap leading-relaxed">
                                            {policy?.terms_description || 'No description set'}
                                        </pre>
                                    </div>
                                )}
                            </div>

                            {/* Action Buttons (when editing) */}
                            {isEditing && (
                                <div className="mt-6 pt-6 border-t border-main-border flex justify-end gap-3">
                                    <button
                                        onClick={() => {
                                            setIsEditing(false);
                                            setEditedPolicy({
                                                terms_title: policy?.terms_title || '',
                                                terms_description: policy?.terms_description || ''
                                            });
                                        }}
                                        className="px-5 py-2.5 rounded-xl font-medium text-main-font bg-main-white border border-main-border hover:bg-main-th transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleSave}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-main-white bg-main-blue hover:bg-main-blue/90 transition-colors"
                                    >
                                        <IoSaveSharp size={18} />
                                        Save Changes
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default IosTerms;